import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  FormGroup,
} from "reactstrap";

import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import toastr from "toastr";
import "flatpickr/dist/themes/material_blue.css";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { post } from "../../helpers/api_helper";
import { TRANSACTION_CREATE } from "../../helpers/url_helper";
import { useNavigate } from "react-router-dom";

const TRANSACTION_TYPE = {
  INCOME: 1,
  EXPENSE: 2,
};

const PAYMENT_TYPE = {
  CASH: 1,
  ONLINE: 2,
};

const TRANSACTION_CATEGORY = {
  BUSINESS_INCOME: 1,
  SALARY: 2,
  OFFICE_EXPENSE: 3,
  OTHER: 4,
};

const AddTransaction = () => {
  const navigate = useNavigate();
  
  const {
  control,
  handleSubmit,
  watch,
  reset,
  setError,
  formState: { errors, isSubmitting },
} = useForm({
  defaultValues: {
    title: "",
    amount: "",
    received_date: new Date().toISOString().slice(0, 10),
    transaction_type: "",
    payment_type: "",
    payment_reference: "",
    source_name: "",
    notes: "",
    category: "",
  },
});

  const watchPaymentType = watch("payment_type");
  const onSubmit = async (data) => {
  try {
    const payload = {
      ...data,
      amount: String(data.amount),
      received_date: data.received_date,
    };

    const response = await post(TRANSACTION_CREATE, payload);
    toastr.success("Transaction created successfully");
    navigate("/transactions");
    reset();
  } catch (error) {
    const apiErrors = error?.response?.data?.errors;
    if (apiErrors) {
      Object.keys(apiErrors).forEach((field) => {
        setError(field, {
          type: "server",
          message: apiErrors[field],
        });
      });
    } else {
      toastr.error(
        error?.response?.data?.message || "Failed to create transaction"
      );
    }
  }
};

  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Create Transaction", link: "#" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Transaction" breadcrumbItems={breadcrumbItems} />
        <Col xl="12">
          <Row>
            <Col xl="12">
              <Card>
                <CardHeader className="bg-light d-flex align-items-center">
                  <span className="bg-white avatar avatar-sm me-2 text-gray flex-shrink-0">
                    <i className="mdi mdi-information-outline text-danger font-size-18" />
                  </span>
                  <h5 className="text-dark m-0">Add Transaction</h5>
                </CardHeader>

                <CardBody>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                      {/* Title */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Title <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="title"
                            control={control}
                            rules={{ required: "Title is required" }}
                            render={({ field }) => (
                              <Input {...field} placeholder="Enter Title" />
                            )}
                          />
                          {errors.title && (
                            <div className="text-danger">
                              {errors.title.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Amount */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Amount <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="amount"
                            control={control}
                            rules={{
                              required: "Amount is required",
                              pattern: {
                                value: /^[0-9]*\.?[0-9]+$/, 
                                message: "Only numeric values allowed",
                              },
                            }}
                            render={({ field }) => (
                              <Input
                              {...field}
                                maxLength={8}
                                type="text"
                                inputMode="decimal"
                                placeholder="Enter Amount"
                                onKeyPress={(e) => {
                                  if (!/[0-9.]$/.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.amount && (
                            <div className="text-danger">{errors.amount.message}</div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Received Date */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Received Date <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="received_date" 
                            control={control}
                            rules={{ required: "Received date is required" }}
                            render={({ field }) => (
                              <Flatpickr
                                className="form-control"
                                options={{
                                  altInput: true,
                                  altFormat: "d-M-Y",
                                  dateFormat: "Y-m-d",
                                }}
                                value={field.value ? new Date(field.value) : new Date()}
                                onChange={(selectedDates) => {
                                if (selectedDates?.[0]) {
                                  // ✅ Correct local date format
                                  const formatted = selectedDates[0].toLocaleDateString("en-CA"); // "YYYY-MM-DD"
                                  field.onChange(formatted);
                                }
                              }}
                              />
                            )}
                          />
                          {errors.received_date && (
                            <div className="text-danger">
                              {errors.received_date.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Transaction Type */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Transaction Type
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="transaction_type"
                            control={control}
                            rules={{ required: "Transaction type is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={[
                                  { value: TRANSACTION_TYPE.INCOME, label: "Income" },
                                  { value: TRANSACTION_TYPE.EXPENSE, label: "Expense" },
                                ]}
                                value={
                                  [
                                    { value: TRANSACTION_TYPE.INCOME, label: "Income" },
                                    { value: TRANSACTION_TYPE.EXPENSE, label: "Expense" },
                                  ].find((opt) => opt.value === field.value) || null
                                }
                                onChange={(e) => field.onChange(e?.value)}
                                placeholder="Select Type"
                              />
                            )}
                          />
                          {errors.transaction_type && (
                            <div className="text-danger">
                              {errors.transaction_type.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Payment Type */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Payment Type <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="payment_type"
                            control={control}
                            rules={{ required: "Payment type is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={[
                                  { value: PAYMENT_TYPE.CASH, label: "Cash" },
                                  { value: PAYMENT_TYPE.ONLINE, label: "Online" },
                                ]}
                                value={[
                                  { value: PAYMENT_TYPE.CASH, label: "Cash" },
                                  { value: PAYMENT_TYPE.ONLINE, label: "Online" },
                                ].find((opt) => opt.value === field.value) || null}
                                onChange={(e) => field.onChange(e?.value)}
                                placeholder="Select Payment Type"
                              />
                            )}
                          />
                          {errors.payment_type && (
                            <div className="text-danger">
                              {errors.payment_type.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Payment Reference - Only if ONLINE */}
                      {watchPaymentType === PAYMENT_TYPE.ONLINE && (
                        <Col xl="3" md="6">
                          <FormGroup>
                            <Label>
                              Payment Reference
                              <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="payment_reference"
                              control={control}
                              rules={{
                                required: "Payment reference is required",
                              }}
                              render={({ field }) => (
                                <Input
                                maxLength={20}
                                  {...field}
                                  placeholder="Enter Payment Reference"
                                />
                              )}
                            />
                            {errors.payment_reference && (
                              <div className="text-danger">
                                {errors.payment_reference.message}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      )}

                      {/* Category */}
                      <Col xl="3" md="6">
                        <FormGroup>
                          <Label>
                            Category <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="category"
                            control={control}
                            rules={{ required: "Category is required" }}
                            render={({ field }) => {
                              const transactionType = watch("transaction_type");

                              const categoryOptions = [
                                {
                                  value: TRANSACTION_CATEGORY.BUSINESS_INCOME,
                                  label: "Business Income",
                                  isDisabled: transactionType === TRANSACTION_TYPE.EXPENSE,
                                },
                                {
                                  value: TRANSACTION_CATEGORY.SALARY,
                                  label: "Salary",
                                  isDisabled: transactionType === TRANSACTION_TYPE.INCOME,
                                },
                                {
                                  value: TRANSACTION_CATEGORY.OFFICE_EXPENSE,
                                  label: "Office Expense",
                                  isDisabled: transactionType === TRANSACTION_TYPE.INCOME,
                                },
                                {
                                  value: TRANSACTION_CATEGORY.OTHER,
                                  label: "Other",
                                  isDisabled: false,
                                },
                              ];

                              return (
                                <Select
                                  {...field}
                                  options={categoryOptions}
                                  value={categoryOptions.find((opt) => opt.value === field.value) || null}
                                  onChange={(e) => field.onChange(e?.value)}
                                  placeholder="Select Category"
                                  isOptionDisabled={(option) => option.isDisabled}
                                />
                              );
                            }}
                          />

                          {errors.category && (
                            <div className="text-danger">
                              {errors.category.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Notes */}
                      <Col xl="3" md="12">
                        <FormGroup>
                          <Label>Source Name</Label>
                          <Controller
                            name="source_name"
                            rules={{ required: "Source name is required" }}
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="text"
                                {...field}
                                placeholder="Enter Source name"
                              />
                            )}
                          />
                          {errors.source_name && (
                            <div className="text-danger">
                              {errors.source_name.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col xl="3" md="12">
                        <FormGroup>
                          <Label>Notes</Label>
                          <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="textarea"
                                rows="3"
                                {...field}
                                placeholder="Enter Notes"
                              />
                            )}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="text-end">
                      <Button type="submit" color="primary" disabled={isSubmitting}>
                          {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
      </Container>
    </div>
  );
};

export default AddTransaction;
